// My Team's side panel, from a tablet up (B-316, DIRECTION U21): beside the
// lineup, one panel that switches between compact versions of the screens a
// roster decision needs — the free agents, the waivers, the trades, the weeks
// ahead and my players' news. Two columns, never three: what sits beside the
// lineup is switched, not added.
//
// A TOOL CAN TAKE THE ROOM (PLAN-2026-10-17 §2.3, B-400): **Expand**, in the
// panel's head, grows it to the whole screen the tool has of its own, at that
// tool's address, while the lineup narrows to a dock beside it; **Collapse**,
// in the same place, gives the room back, and so does Back. It was an
// icon-only arrow at the end of the switcher that opened the tool's screen in
// place of My Team, which read as more tabs (B-401, UX-41).
//
// A PLAYER'S CARD OPENS HERE, over the list it came from, which keeps its
// place under it for Back: from a name on the lineup, in a panel, or anywhere
// else on the page (`PlayerCardsOpenIn`). A sheet would cover the roster the
// card is being read against.
//
// A LINEUP ROW CHOOSES ITS SLOT (B-320): Players then lists who can fill it,
// ranked, until the slot is put back; the name on the row is still the card.
//
// Another manager's team has one panel, their weeks ahead, and their
// players' cards open in it the same way; in the tour's practice league,
// which has no weeks ahead, it has no side panel at all (the screen's call).

import { useCallback, useState } from 'react';
import { useTier } from '../../shell/useTier';
import { useLeagueSource } from '../../data/leagueSource';
import { Panel } from '../../kit/Panel';
import { Button, Segments } from '../../kit/ui';
import { PlayerCardBody, type PlayerCardData } from '../shared/PlayerCard';
import type { RosterMove } from '../shared/RosterMoveSheet';
import { FreeAgentsPanel } from './FreeAgentsPanel';
import { NewsPanel } from './NewsPanel';
import { PlanPanel } from './PlanPanel';
import { SlotPanel, type ChosenSlot } from './SlotPanel';
import { TradesPanel, type TradeAsk } from './TradesPanel';
import { WaiversPanel } from './WaiversPanel';
import { ToolRoom } from './ToolRoom';

export type SideTool = 'players' | 'waivers' | 'trades' | 'plan' | 'news';

/** The tools, in the switcher's order, with the address each takes the room at. */
export const TOOLS: readonly { id: SideTool; label: string; title: string; path: string }[] = [
  { id: 'players', label: 'Players', title: 'Free agents', path: '/players' },
  { id: 'waivers', label: 'Waivers', title: 'Waivers', path: '/waivers' },
  { id: 'trades', label: 'Trades', title: 'Trades', path: '/trades' },
  { id: 'plan', label: 'Plan', title: 'Plan ahead', path: '/plan' },
  { id: 'news', label: 'News', title: 'Your players’ news', path: '/news' },
];

export function TeamSide({
  teamId,
  mine,
  tool,
  onTool,
  card,
  onCard,
  onMove,
  counts,
  slot,
  onSlot,
  onPlace,
  room = false,
  onRoom,
}: {
  /** The team on screen. */
  teamId: string;
  /** Mine to manage, with every panel; another team's has its weeks ahead. */
  mine: boolean;
  tool: SideTool;
  onTool: (tool: SideTool) => void;
  /** The card open in the panel, or null for the panel's list. */
  card: PlayerCardData | null;
  onCard: (card: PlayerCardData | null) => void;
  /** Add, claim or drop: the screen's own roster-move sheet. */
  onMove: (move: RosterMove) => void;
  /** What waits on me, for the switcher's counts. */
  counts: { claims: number; offers: number };
  /** The lineup's slot chosen (B-320): Players lists who can fill it. Null for the free agents. */
  slot: ChosenSlot | null;
  onSlot: (slot: ChosenSlot | null) => void;
  /** Move a player of mine into the chosen slot (B-379), as `SlotPanel` takes it. */
  onPlace?: (playerId: string) => (() => void) | null;
  /** The tool has taken the room: its whole screen, beside the dock. */
  room?: boolean;
  /** Take the room, or give it back; absent where the tool cannot (another team's). */
  onRoom?: (room: boolean) => void;
}) {
  const tier = useTier();
  // A card's Trade for, answered by the Trades panel's builder.
  const [ask, setAsk] = useState<TradeAsk | null>(null);
  const asked = useCallback(() => setAsk(null), []);
  // PLAN READS A REAL LEAGUE'S SCHEDULE; the tour's simulator plays one
  // week, so it has no Plan to offer (the phone's doors leave it out too).
  const real = useLeagueSource() === 'api';
  const tools = real ? TOOLS : TOOLS.filter((t) => t.id !== 'plan');
  const shown: SideTool = !mine ? 'plan' : tools.some((t) => t.id === tool) ? tool : 'players';
  const current = TOOLS.find((t) => t.id === shown)!;

  // Esc puts the card away; the screen's one Esc handler does it, after a
  // move and never over a sheet (LineupScreen, the review of 2026-10-13).

  // EXPAND AND COLLAPSE SAY WHAT THEY DO (B-401): a word beside its picture,
  // never an arrow alone, which means the next in a sequence (UX-41).
  const grow =
    onRoom !== undefined ? (
      <Button
        size="xs"
        variant="quiet"
        icon={room ? 'shrink' : 'grow'}
        onClick={() => onRoom(!room)}
        // Its name starts with its word, and says what grows (WCAG 2.5.3).
        label={room ? `Collapse ${current.title}` : `Expand ${current.title}`}
      >
        {room ? 'Collapse' : 'Expand'}
      </Button>
    ) : null;
  const switcher = (
    <Segments
      label="Beside the lineup"
      items={tools.map((t) => ({
        id: t.id,
        label: t.label,
        count: t.id === 'waivers' ? counts.claims : t.id === 'trades' ? counts.offers : 0,
        tone: 'warn' as const,
      }))}
      value={shown}
      onChange={onTool}
    />
  );
  // A TABLET'S PANEL BESIDE THE LINEUP has no room for a word beside five
  // tabs, so Expand has a row of its own under them, with the tool's name.
  const stacked = tier === 'tablet' && !room;
  const heading =
    card !== null ? (
      <div className="fsp-side-head">
        <Button size="xs" variant="quiet" icon="back" onClick={() => onCard(null)}>
          {mine ? current.label : current.title}
        </Button>
      </div>
    ) : mine && stacked ? (
      <div className="fsp-side-head fsp-side-head-stacked">
        <span className="fsp-grow">{switcher}</span>
        <span className="fsp-side-head-row">
          <span className="fsp-cap fsp-grow">{current.title}</span>
          {grow}
        </span>
      </div>
    ) : mine ? (
      <div className="fsp-side-head">
        <span className="fsp-grow">{switcher}</span>
        {grow}
      </div>
    ) : undefined;

  const filling = mine && shown === 'players' && slot !== null;
  const body =
    shown === 'players' ? (
      filling ? (
        <SlotPanel
          key={`${slot.slot}-${slot.slotIndex}`}
          chosen={slot}
          onOpen={onCard}
          onMove={onMove}
          onPlace={onPlace}
          onClose={() => onSlot(null)}
        />
      ) : room ? (
        <ToolRoom tool="players" />
      ) : (
        <FreeAgentsPanel onOpen={onCard} onMove={onMove} />
      )
    ) : room ? (
      <ToolRoom tool={shown} />
    ) : shown === 'waivers' ? (
      <WaiversPanel onOpen={onCard} onMove={onMove} />
    ) : shown === 'trades' ? (
      <TradesPanel ask={ask} onAsked={asked} />
    ) : shown === 'plan' ? (
      <PlanPanel teamId={teamId} onOpen={onCard} />
    ) : (
      <NewsPanel />
    );

  return (
    <Panel
      title={card !== null ? 'Player' : filling ? 'Who can fill this slot' : current.title}
      heading={heading}
      fill
      className={`fsp-team-side${room ? ' fsp-team-room' : ''}`}
      bodyClass={room && !filling ? 'fsp-room-body' : undefined}
      over={
        card !== null ? (
          <div key={card.player.id} className="fsp-side-card">
            <PlayerCardBody
              data={card}
              onClose={() => onCard(null)}
              onMove={onMove}
              onTrade={
                mine
                  ? (team, playerId) => {
                      onTool('trades');
                      setAsk({ teamId: team, playerId });
                    }
                  : undefined
              }
            />
          </div>
        ) : null
      }
    >
      {body}
    </Panel>
  );
}
